const CART_STORAGE_KEY = 'cart';

const safeParse = (value, fallback = []) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.error('Failed to parse cart from storage', error);
    return fallback;
  }
};

const saveCart = (items) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Failed to save cart to storage', error);
  }
};

const loadCart = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  return safeParse(localStorage.getItem(CART_STORAGE_KEY));
};

const getNumericPrice = (price) => {
  if (typeof price === 'number') return price;
  if (typeof price === 'string') {
    const parsed = parseFloat(price.replace(/[^\d.]/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (price && typeof price === 'object') {
    if (typeof price.amount === 'number') return price.amount;
    if (typeof price.formatted === 'string') {
      const parsed = parseFloat(price.formatted.replace(/[^\d.]/g, ''));
      return Number.isNaN(parsed) ? 0 : parsed;
    }
  }
  return 0;
};

const calculateTotals = (items) =>
  items.reduce(
    (acc, item) => ({
      total: acc.total + getNumericPrice(item.price) * (item.qty || 0),
      quantity: acc.quantity + (item.qty || 0),
    }),
    { total: 0, quantity: 0 }
  );

const getCartState = () => {
  const items = loadCart();
  const { total, quantity } = calculateTotals(items);
  return { items, total, quantity };
};

const normalizeProduct = (product) => {
  if (!product) {
    throw new Error('Invalid product reference');
  }

  const variantId = product.variant_id || product.variantId || null;

  return {
    productId: product.productId || product.id,
    name: product.name || product.title || 'Product',
    price:
      typeof product.price !== 'undefined'
        ? product.price
        : product.selling_price?.amount ||
        product.selling_price?.formatted ||
        product.price?.amount ||
        product.price?.formatted ||
        0,
    image: product.image || product.base_image?.path || product.media?.[0]?.path || '/placeholder.jpg',
    qty: product.quantity || product.qty || 1,
    variant_id: variantId,
    options: product.options || [],
    // Physical weight in kg — extracted from product specs at add-to-cart time.
    // Used by the shipping API (items[].weight_kg). null = not available.
    weight_kg: (typeof product.weight_kg === 'number' && product.weight_kg > 0)
      ? product.weight_kg
      : null,
  };
};

const findItemIndex = (items, productId, variantId = null) =>
  items.findIndex(
    (item) =>
      item.productId === productId &&
      ((item.variant_id || null) === (variantId || null))
  );

const addItem = (product, quantity = 1) => {
  const normalized = normalizeProduct({ ...product, quantity });
  if (!normalized.productId) {
    throw new Error('Product ID is required');
  }

  const items = loadCart();
  const index = findItemIndex(items, normalized.productId, normalized.variant_id);

  if (index > -1) {
    items[index].qty += quantity;
  } else {
    items.push({ ...normalized, qty: quantity });
  }

  saveCart(items);
  return getCartState();
};

const updateItemQuantity = (productId, quantity, variantId = null) => {
  if (!productId) {
    throw new Error('Product ID is required');
  }
  if (quantity < 0) {
    throw new Error('Quantity cannot be negative');
  }

  const items = loadCart();
  const index = findItemIndex(items, productId, variantId);

  if (index === -1) {
    throw new Error('Item not found in cart');
  }

  if (quantity === 0) {
    items.splice(index, 1);
  } else {
    items[index].qty = quantity;
  }

  saveCart(items);
  return getCartState();
};

const removeItem = (productId, variantId = null) => {
  if (!productId) {
    throw new Error('Product ID is required');
  }
  const items = loadCart().filter(
    (item) =>
      item.productId !== productId ||
      (item.variant_id || null) !== (variantId || null)
  );

  saveCart(items);
  return getCartState();
};

const clearCart = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear cart storage', error);
  }
  return { items: [], total: 0, quantity: 0 };
};

const cartService = {
  getCartState,
  addItem,
  updateItemQuantity,
  removeItem,
  clearCart,
  loadCartItems: loadCart,
  saveCartItems: saveCart,
};

export default cartService;

